import { V1Rule, V1RuleSet } from 'next-gen-sdk'
import {
  cloneAndNormalizeRule,
  cloneAndNormalizeRuleSet,
} from './ruleSetNormalization'

/**
 * Simple deep equality check using JSON serialization.
 * This is used as a fallback comparison method after normalization.
 *
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @returns true if objects are deeply equal, false otherwise
 *
 * @example
 * ```typescript
 * const obj1 = { id: '1', term: 'test' }
 * const obj2 = { id: '1', term: 'test' }
 * isEqual(obj1, obj2) // returns true
 * ```
 */
const isEqual = (obj1: any, obj2: any): boolean => {
  try {
    return JSON.stringify(obj1) === JSON.stringify(obj2)
  } catch {
    return false
  }
}

/**
 * Compares two rules to determine if they have changed.
 *
 * This function performs a deep comparison of rule properties after normalization
 * to detect any meaningful changes. Rules without IDs are considered new and
 * always return true for changes.
 *
 * @param original - The original rule (may be undefined for new rules)
 * @param modified - The modified rule to compare against
 * @returns true if the rule has changed, false if identical
 *
 * @example
 * ```typescript
 * const original = { id: '1', term: 'test', type: 'term' }
 * const modified = { id: '1', term: 'modified', type: 'term' }
 * hasRuleChanged(original, modified) // returns true
 *
 * const unchanged = { id: '1', term: 'test', type: 'term' }
 * hasRuleChanged(original, unchanged) // returns false
 *
 * const newRule = { term: 'new', type: 'term' } // no ID
 * hasRuleChanged(original, newRule) // returns true (new rule)
 * ```
 */
export const hasRuleChanged = (
  original: V1Rule | undefined,
  modified: V1Rule | undefined,
): boolean => {
  // Handle null/undefined cases
  if (!original && !modified) return false
  if (!original || !modified) return true

  // If the rule has no ID, it's a new rule (always changed)
  if (!modified.id) return true

  const normalizedOriginal = cloneAndNormalizeRule(original)
  const normalizedModified = cloneAndNormalizeRule(modified)

  return !isEqual(normalizedOriginal, normalizedModified)
}

/**
 * Compares arrays of rules to find changes
 * @param originalRules - Array of original rules
 * @param modifiedRules - Array of modified rules
 * @returns Object containing change information
 */
export const compareRulesArrays = (
  originalRules: V1Rule[] = [],
  modifiedRules: V1Rule[] = [],
): {
  hasChanges: boolean
  newRules: V1Rule[]
  changedRules: V1Rule[]
  deletedRules: V1Rule[]
} => {
  // Handle null/undefined arrays
  const safeOriginalRules = Array.isArray(originalRules) ? originalRules : []
  const safeModifiedRules = Array.isArray(modifiedRules) ? modifiedRules : []

  const newRules = safeModifiedRules.filter((rule) => rule && !rule.id)
  const deletedRules = safeOriginalRules.filter(
    (originalRule) =>
      originalRule &&
      !safeModifiedRules.some(
        (modifiedRule) => modifiedRule && modifiedRule.id === originalRule.id,
      ),
  )

  const changedRules = safeModifiedRules.filter((modifiedRule) => {
    if (!modifiedRule || !modifiedRule.id) return false // New rules are handled separately

    const originalRule = safeOriginalRules.find(
      (rule) => rule && rule.id === modifiedRule.id,
    )
    return hasRuleChanged(originalRule, modifiedRule)
  })

  const hasChanges =
    newRules.length > 0 || changedRules.length > 0 || deletedRules.length > 0

  return {
    hasChanges,
    newRules,
    changedRules,
    deletedRules,
  }
}

/**
 * Compares two rule sets to determine if they have changed
 * @param original - The original rule set (may be undefined for new rule sets)
 * @param modified - The modified rule set
 * @returns true if the rule set has changed, false otherwise
 */
export const hasRuleSetChanged = (
  original: V1RuleSet | undefined,
  modified: V1RuleSet | undefined,
): boolean => {
  // Handle null/undefined cases
  if (!original && !modified) return false
  if (!original || !modified) return true

  // If the rule set has no ID, it's a new rule set (always changed)
  if (!modified.id) return true

  const normalizedOriginal = cloneAndNormalizeRuleSet(original)
  const normalizedModified = cloneAndNormalizeRuleSet(modified)

  if (!normalizedOriginal || !normalizedModified) return true

  // Check if operator changed
  if (normalizedOriginal.operator !== normalizedModified.operator) {
    return true
  }

  // Check if rules array changed
  const rulesComparison = compareRulesArrays(
    normalizedOriginal.rules,
    normalizedModified.rules,
  )
  if (rulesComparison.hasChanges) {
    return true
  }

  // Check if nested rule sets changed
  const nestedRuleSetsChanged = hasNestedRuleSetChanged(
    normalizedOriginal.ruleSet || [],
    normalizedModified.ruleSet || [],
  )
  if (nestedRuleSetsChanged) {
    return true
  }

  return false
}

/**
 * Compares arrays of nested rule sets to determine if they have changed
 * @param original - Array of original rule sets
 * @param modified - Array of modified rule sets
 * @returns true if any nested rule set has changed, false otherwise
 */
export const hasNestedRuleSetChanged = (
  original: V1RuleSet[] = [],
  modified: V1RuleSet[] = [],
): boolean => {
  // Check for new rule sets (no ID)
  const hasNewRuleSets = modified.some((ruleSet) => !ruleSet.id)
  if (hasNewRuleSets) return true

  // Check for deleted rule sets
  const hasDeletedRuleSets = original.some(
    (originalRuleSet) =>
      !modified.some(
        (modifiedRuleSet) => modifiedRuleSet.id === originalRuleSet.id,
      ),
  )
  if (hasDeletedRuleSets) return true

  // Check for changes in existing rule sets
  return modified.some((modifiedRuleSet) => {
    if (!modifiedRuleSet.id) return false // New rule sets are handled above

    const originalRuleSet = original.find(
      (ruleSet) => ruleSet.id === modifiedRuleSet.id,
    )
    return hasRuleSetChanged(originalRuleSet, modifiedRuleSet)
  })
}

/**
 * Gets detailed information about what has changed in a rule set
 * @param original - The original rule set
 * @param modified - The modified rule set
 * @returns Object containing detailed change information
 */
export const getRuleSetChanges = (
  original: V1RuleSet | undefined,
  modified: V1RuleSet,
): {
  ruleSetChanged: boolean
  operatorChanged: boolean
  rulesChanged: {
    hasChanges: boolean
    newRules: V1Rule[]
    changedRules: V1Rule[]
    deletedRules: V1Rule[]
  }
  nestedRuleSetsChanged: boolean
} => {
  if (!original) {
    return {
      ruleSetChanged: true,
      operatorChanged: true,
      rulesChanged: {
        hasChanges: true,
        newRules: modified.rules || [],
        changedRules: [],
        deletedRules: [],
      },
      nestedRuleSetsChanged: true,
    }
  }

  const normalizedOriginal = cloneAndNormalizeRuleSet(original)
  const normalizedModified = cloneAndNormalizeRuleSet(modified)

  if (!normalizedOriginal || !normalizedModified) {
    return {
      ruleSetChanged: true,
      operatorChanged: true,
      rulesChanged: {
        hasChanges: true,
        newRules: modified.rules || [],
        changedRules: [],
        deletedRules: [],
      },
      nestedRuleSetsChanged: true,
    }
  }

  const operatorChanged =
    normalizedOriginal.operator !== normalizedModified.operator
  const rulesChanged = compareRulesArrays(
    normalizedOriginal.rules,
    normalizedModified.rules,
  )
  const nestedRuleSetsChanged = hasNestedRuleSetChanged(
    normalizedOriginal.ruleSet || [],
    normalizedModified.ruleSet || [],
  )

  const ruleSetChanged =
    operatorChanged || rulesChanged.hasChanges || nestedRuleSetsChanged

  return {
    ruleSetChanged,
    operatorChanged,
    rulesChanged,
    nestedRuleSetsChanged,
  }
}
